import { useState } from 'react';
import { Modal, Button, Badge } from '@react-apps/design-system';
import { Tooltip } from '@/shared/components/tooltip';
import { EventActor } from '@/shared/components/event-actor';
import {
  useUserEntityEvents,
  type UserTimelineEvent,
} from '../hooks/use-user-entity-events';

const PAGE_SIZE = 10;

const SOURCE_LABEL: Record<UserTimelineEvent['source'], string> = {
  Account: 'Conta',
  User: 'Perfil',
  PlatformProfile: 'Plataforma',
  GrantedPerk: 'Acesso',
};

const SOURCE_BADGE: Record<
  UserTimelineEvent['source'],
  'default' | 'info' | 'success'
> = {
  Account: 'info',
  User: 'default',
  PlatformProfile: 'default',
  GrantedPerk: 'success',
};

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(iso));
}

/** Deriva um rótulo legível a partir do `eventType` bruto (ex.: `USER_NAME_UPDATED` -> `User name updated`). */
function humanizeEventType(eventType: string): string {
  const words = eventType.toLowerCase().split('_');
  return (words[0] ?? '').charAt(0).toUpperCase() + words.join(' ').slice(1);
}

function str(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.trim() !== '' ? value : '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return JSON.stringify(value);
}

interface FieldChange {
  oldValue: unknown;
  newValue: unknown;
}

function fieldChanges(
  payload: Record<string, unknown>,
): Record<string, FieldChange> | null {
  const changes = payload.changes;
  if (!changes || typeof changes !== 'object') return null;
  return changes as Record<string, FieldChange>;
}

function ValuePill({ children }: { children: string }) {
  return (
    <span className="border-(--color-border) bg-(--color-bg-primary) text-(--color-text-primary) inline-block min-w-0 max-w-full whitespace-pre-wrap break-words rounded-md border px-2 py-0.5 font-mono text-xs">
      {children}
    </span>
  );
}

function eventJson(event: UserTimelineEvent): string {
  return JSON.stringify(
    {
      id: event.id,
      source: event.source,
      eventType: event.eventType,
      actorPlatformProfileId: event.actorPlatformProfileId,
      correlationId: event.correlationId ?? null,
      reason: event.reason,
      createdAt: event.createdAt,
      perkKey: event.perkKey ?? null,
      payload: event.payload,
    },
    null,
    2,
  );
}

function EventRow({ event }: { event: UserTimelineEvent }) {
  const label = humanizeEventType(event.eventType);
  const changes = fieldChanges(event.payload);

  return (
    <li className="border-(--color-border) bg-(--color-bg-secondary) space-y-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={SOURCE_BADGE[event.source]}>
            {SOURCE_LABEL[event.source]}
          </Badge>
          <span className="text-(--color-text-primary) text-sm font-medium">
            {label}
          </span>
          {event.perkKey && (
            <span className="border-(--color-border) bg-(--color-bg-primary) text-(--color-text-secondary) rounded-md border px-2 py-0.5 font-mono text-xs">
              {event.perkKey}
            </span>
          )}
        </div>
        <span className="text-(--color-text-secondary) text-xs">
          {formatDateTime(event.createdAt)}
        </span>
      </div>

      {changes && Object.keys(changes).length > 0 && (
        <div className="space-y-1.5">
          {Object.entries(changes).map(([field, change]) => (
            <div key={field} className="flex flex-wrap items-start gap-2">
              <span className="text-(--color-text-secondary) py-0.5 text-xs font-medium">
                {field}
              </span>
              <ValuePill>{str(change.oldValue)}</ValuePill>
              <span className="text-(--color-text-secondary) py-0.5">→</span>
              <ValuePill>{str(change.newValue)}</ValuePill>
            </div>
          ))}
        </div>
      )}

      <div className="text-(--color-text-secondary) flex flex-wrap items-center gap-2 text-xs">
        <EventActor event={event} />
        {event.reason && <span>· {event.reason}</span>}
        {event.correlationId && (
          <Tooltip message="Row de inbox de origem" trigger="hover">
            · origem <span className="font-mono">{event.correlationId}</span>
          </Tooltip>
        )}
      </div>

      <details className="group">
        <summary className="text-(--color-accent) cursor-pointer select-none text-xs font-medium">
          Ver payload (JSON)
        </summary>
        <pre className="border-(--color-border) bg-(--color-bg-primary) text-(--color-text-secondary) mt-2 max-h-72 overflow-auto rounded-lg border p-3 text-xs leading-relaxed">
          {eventJson(event)}
        </pre>
      </details>
    </li>
  );
}

export function UserEntityEventsModal({
  accountId,
  userId,
  platformProfileIds,
  open,
  onClose,
}: {
  accountId: string;
  userId: string;
  platformProfileIds: string[];
  open: boolean;
  onClose: () => void;
}) {
  const [perPage, setPerPage] = useState(PAGE_SIZE);
  const { events, total, hasMore, isLoading, isFetching, isError } =
    useUserEntityEvents(accountId, userId, platformProfileIds, perPage, open);

  const handleClose = () => {
    setPerPage(PAGE_SIZE);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Histórico de eventos"
      description="Eventos de auditoria da conta, do perfil, das plataformas e dos acessos deste usuário, dos mais recentes aos mais antigos."
      size="md"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-(--color-text-secondary) text-xs">
            {events.length} de {total} {total === 1 ? 'evento' : 'eventos'}
          </span>
          <Button
            variant="secondary"
            onClick={() => setPerPage((p) => p + PAGE_SIZE)}
            disabled={!hasMore || isFetching}
          >
            Carregar mais
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <ul className="space-y-3">
          {Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <li
              key={i}
              className="border-(--color-border) bg-(--color-bg-secondary) h-20 animate-pulse rounded-lg border"
            />
          ))}
        </ul>
      ) : isError ? (
        <p className="text-(--color-text-secondary) py-8 text-center text-sm">
          Não foi possível carregar os eventos.
        </p>
      ) : events.length === 0 ? (
        <p className="text-(--color-text-secondary) py-8 text-center text-sm">
          Nenhum evento registrado para este usuário.
        </p>
      ) : (
        <ul className="space-y-3">
          {events.map((event) => (
            <EventRow key={`${event.source}-${event.id}`} event={event} />
          ))}
        </ul>
      )}
    </Modal>
  );
}

/**
 * Botão auto-contido: abre num modal o histórico combinado de eventos
 * (Account + User + perfis de plataforma + concessões de perk).
 */
export function UserEntityEventsButton({
  accountId,
  userId,
  platformProfileIds,
}: {
  accountId: string;
  userId: string;
  platformProfileIds: string[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        Histórico
      </Button>
      <UserEntityEventsModal
        accountId={accountId}
        userId={userId}
        platformProfileIds={platformProfileIds}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
